import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ABOUT_PAGES, UI } from "@/data/content";

const renderRich = (text) =>
  text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).map((part, i) => {
    if (part.startsWith("**")) return <strong key={i} className="font-semibold text-[#F4EFEA]">{part.slice(2, -2)}</strong>;
    if (part.startsWith("*")) return <em key={i} className="text-[#B03A52]">{part.slice(1, -1)}</em>;
    return part;
  });

const toBlocks = (paras) => {
  const blocks = [];
  let list = [];
  paras.forEach((p) => {
    if (p.startsWith("• ")) {
      list.push(p.slice(2));
    } else {
      if (list.length) blocks.push({ type: "ul", items: list });
      list = [];
      blocks.push({ type: "p", text: p });
    }
  });
  if (list.length) blocks.push({ type: "ul", items: list });
  return blocks;
};

export default function About({ lang }) {
  const t = UI[lang];
  const [activeId, setActiveId] = useState("artist");
  const active = ABOUT_PAGES.find((p) => p.id === activeId);

  return (
    <section id="about" data-testid="about-section" className="relative px-6 sm:px-12 md:px-20 py-28 border-t border-[#7E2438]/25">
      <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#B03A52]">{t.aboutEyebrow}</span>
      <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl tracking-tight font-serif text-[#F4EFEA]">{t.aboutTitle}</h2>

      <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 border-b border-[#7E2438]/25">
        {ABOUT_PAGES.map((p) => (
          <button
            key={p.id}
            data-testid={`about-tab-${p.id}`}
            onClick={() => setActiveId(p.id)}
            className={`relative pb-4 font-serif text-lg sm:text-xl transition-colors duration-300 ${
              activeId === p.id ? "text-[#F4EFEA]" : "text-[#A89A9E] hover:text-[#F4EFEA]/70"
            }`}
          >
            {p.label[lang]}{p.id === "artist" ? "." : ""}
            {activeId === p.id && (
              <motion.span layoutId="about-underline" className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#B03A52]" />
            )}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeId + lang}
          data-testid={`about-content-${activeId}`}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 grid lg:grid-cols-[1fr_0.7fr] gap-10 lg:gap-14 items-start"
        >
          <div className="space-y-6 max-w-2xl">
            {toBlocks(active.paragraphs[lang]).map((block, bi) =>
              block.type === "ul" ? (
                <ul key={bi} className="space-y-2.5 text-base sm:text-lg leading-relaxed text-[#A89A9E]">
                  {block.items.map((item, ii) => (
                    <li key={ii} className="flex gap-3">
                      <span className="text-[#B03A52] shrink-0">•</span>
                      <span>{renderRich(item)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p key={bi} className={`leading-relaxed ${active.id === "artist" || (bi === 0 && active.id !== "formation") ? "font-serif text-xl sm:text-2xl text-[#F4EFEA]" : "text-base sm:text-lg text-[#A89A9E]"}`}>
                  {renderRich(block.text)}
                </p>
              )
            )}
          </div>
          {active.logos ? (
            <div data-testid={`about-logos-${activeId}`} className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md w-full">
              {active.logos.map((logo, i) => (
                <div
                  key={i}
                  data-testid={`about-logo-${i}`}
                  className="flex aspect-[4/3] items-center justify-center border border-[#7E2438]/25 bg-[#F4EFEA] p-2 transition-transform duration-300 hover:-translate-y-1"
                >
                  <img src={logo.src} alt={logo.alt} className="h-full w-full object-contain" />
                </div>
              ))}
            </div>
          ) : (
            <div className="relative max-w-sm w-full">
              <img
                data-testid={`about-image-${activeId}`}
                src={active.img}
                alt={active.label[lang]}
                className={`relative w-full ${active.frame === "square" ? "aspect-square" : "aspect-[4/5]"} object-cover ${active.fullColor ? "" : "grayscale-[30%]"}`}
              />
              {!active.fullColor && <div className="absolute inset-0 bg-gradient-to-t from-[#7E2438]/30 to-transparent" />}
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </section>
  );
}
